import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {ActionSheet, Button, NavBar, Image} from 'antd-mobile';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from "../../../../utils/deviceUtils";

import {PickupAppointmentType} from "../../graphql/types/PickupTaskWithAppointmentsType";
import {
    getUnitLabel,
    PickupCommonType,
    UnitOptions,
    getPickupFailureReasonText,
    getBusinessDaysText
} from "../../../../enums/PickupEnum";
import PickUpPhotoViewer from '../../components/PickUpPhotoViewer';
import {formatTimestampToDateTime, formatTimestampByLanguage} from "../../../../utils/dateTimeUtils";
import {useCompletedPickupTaskAppointment} from "../../store/pickupStore";
import i18n from "../../../../i18n";
import {PickupFinishedPath} from "../../../../enums/PickUpNavigatePathEnum";
import {buildAddressFromAppointment} from "../../../../utils/addressUtils";

interface PhotoRequirement {
    description: string;
    items: string[];
}

const PickupFinishedDetailPage: React.FC = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const { appointmentId, taskId, selectedDate } = useParams<{
        appointmentId: string;
        taskId: string;
        selectedDate: string;
    }>();
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const {pickupTaskAppointment, loading} = useCompletedPickupTaskAppointment(
        appointmentId || '', 
        taskId || ''
    );
    const [pickUpData, setPickUpData] = useState<PickupAppointmentType>();
    // 揽收成功
    const pickUpSuccess = pickUpData?.isFinished && !pickUpData?.isAbnormal;
    // 揽收失败
    const pickUpFailed = pickUpData?.isFinished && pickUpData?.isAbnormal;
    const startPoint = pickUpData?.status === 'start_point';
    const endPoint = pickUpData?.status === 'end_point';
    const [successActionSheetVisible, setSuccessActionSheetVisible] = useState(false);
    const successSelectOps:PickupCommonType[] = UnitOptions;
    const pickUpDetailPhotos = (() => {
        if (endPoint) {
            return pickUpData?.unloadingPhotosUrls || [];  // 使用卸货照片
        } else if (pickUpSuccess) {
            return pickUpData?.photosUrls || [];
        } else if (pickUpFailed) {
            return pickUpData?.exceptionPhotosUrls || [];
        }
        return [];
    })();
    
    // 处理揽收成功单位选择
    const handleUnitSelection = (unitValue: number) => {
        setSuccessActionSheetVisible(false);
        // 跳转到揽收成功页面，传递选择的单位信息和当前选择的日期
        navigate('/pickup/to-picked-up', {
            state: {
                appointmentId: pickUpData?.id,
                taskId: pickUpData?.taskId,
                selectedUnit: unitValue,
                selectedDate: selectedDate
            }
        });
    };

    useEffect(() => {
        if (pickupTaskAppointment) {
            setPickUpData(pickupTaskAppointment);
        }
    }, [pickupTaskAppointment, pickUpData?.sequence]);

    // 只在loading为true且没有mock数据时返回null
    if (loading && !pickUpData) {
        return null;
    }

    // 确保真的没有任何数据时才返回null
    if (!pickUpData && !loading) {
        return null;
    }

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    const renderDetailInfo = () => {
        const businessDaysStr = getBusinessDaysText(pickUpData?.businessDays || [], i18n.language);

        const commonFields = (
            <>
                <div className="flex justify-between items-center mb-4">
                    <div className="text-gray-500">{t('pages.pickUp.finished.name')}:</div>
                    <div className="text-right break-all">{pickUpData?.channelName}</div>
                </div>
                <div className="flex justify-between items-center mb-4">
                    <div className="text-gray-500">{t('pages.pickUp.finished.code')}:</div>
                    <div className="text-right">{pickUpData?.sequence}</div>
                </div>
                <div className="flex justify-between items-center mb-4">
                    <div className="text-gray-500">{t('pages.pickUp.finished.contact')}:</div>
                    <div className="text-right">{`${pickUpData?.contactName}`} </div>
                </div>
                <div className="flex justify-between items-center mb-4">
                    <div className="text-gray-500">{t('pages.pickUp.finished.phone')}:</div>
                    <div className="text-right">{`${pickUpData?.contactPhone}${pickUpData?.ext ? `-${pickUpData?.ext}` : ''}`} </div>
                </div>
                <div className="flex justify-between items-start mb-4">
                    <div className="text-gray-500">{t('pages.pickUp.finished.address')}:</div>
                    <div className="text-right flex-1 ml-4 break-all">
                        {buildAddressFromAppointment(pickUpData || {})}
                    </div>
                </div>
                <div className="flex justify-between items-center mb-4">
                    <div className="text-gray-500">{t('pages.pickUp.pickedUp.businessHours')}:</div>
                    <div className="text-right">{businessDaysStr}</div>
                </div>
                <div className="flex justify-between items-center mb-4">
                    <div className="text-gray-500"></div>
                    <div className="text-right">{pickUpData?.businessHours}</div>
                </div>
            </>
        );
        if (endPoint) {
            return (
                <div className="p-4 bg-white mb-1">
                    <div className="text-lg font-bold mb-4">{t('pages.pickUp.finished.siteInfo')}</div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.name')}:</div>
                        <div className="text-right">{pickUpData?.site}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.code')}:</div>
                        <div className="text-right">{t('pages.pickUp.end')}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.phone')}:</div>
                        <div className="text-right">{`${pickUpData?.contactPhone}${pickUpData?.ext ? `-${pickUpData?.ext}` : ''}`} </div>
                    </div>
                    <div className="flex justify-between items-start mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.address')}:</div>
                        <div className="text-right flex-1 ml-4 break-all">
                            {buildAddressFromAppointment(pickUpData || {})}
                        </div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.scheduledHandoverTime')}:</div>
                        <div className="text-right">{formatTimestampByLanguage(pickUpData?.appointmentTime, i18n.language)}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500 text-[#6366f1]">{t('pages.pickUp.finished.handoverCompletedTime')}:</div>
                        <div className="text-right text-[#6366f1]">{formatTimestampByLanguage(pickUpData?.unloadingTime, i18n.language)}</div>
                    </div>
                </div>
            );
        }

        if (pickUpSuccess) {
            const businessDaysStr = getBusinessDaysText(pickUpData?.businessDays || [], i18n.language);
            return (
                <div className="p-4 bg-white mb-1">
                    <div className="text-lg font-bold mb-4">{t('pages.pickUp.finished.detailInfo')}</div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.name')}:</div>
                        <div className="text-right break-all">{pickUpData?.channelName}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.code')}:</div>
                        <div className="text-right">{pickUpData?.sequence}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.contact')}:</div>
                        <div className="text-right">{pickUpData?.contactName}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.phone')}:</div>
                        <div className="text-right">{`${pickUpData?.contactPhone}${pickUpData?.ext ? `-${pickUpData?.ext}` : ''}`} </div>
                    </div>
                    <div className="flex justify-between items-start mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.address')}:</div>
                        <div className="text-right flex-1 ml-4 break-all">
                            {buildAddressFromAppointment(pickUpData || {})}
                        </div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.pickingUp.appointmentPickUp')}:</div>
                        <div className="text-right">{pickUpData?.quantity || 0} {t('pages.pickUp.pickingUp.appointmentPickUpUnit')}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.pickedUp.businessHours')}:</div>
                        <div className="text-right">{businessDaysStr}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500"></div>
                        <div className="text-right">{pickUpData?.businessHours}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.scheduledPickupTime')}:</div>
                        <div className="text-right">{formatTimestampByLanguage(pickUpData?.appointmentTime, i18n.language)}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.pickupCompletedTime')}:</div>
                        <div className="text-right text-[#6366f1]">{formatTimestampByLanguage(pickUpData?.actualPickupTime, i18n.language)}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.actualPickup')}:</div>
                        <div className="text-right text-[#6366f1]">{pickUpData?.actualQuantity || 0}{getUnitLabel(Number(pickUpData?.actualUnit) || 1, i18n.language)}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.remark')}:</div>
                        <div className="text-right break-all">{pickUpData?.remark || '-'}</div>
                    </div>
                </div>
            );
        }

        if (pickUpFailed) {
            return (
                <div className="p-4 bg-white mb-1">
                    <div className="text-lg font-bold mb-4">{t('pages.pickUp.finished.detailInfo')}</div>
                    {commonFields}
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.finished.scheduledPickupTime')}:</div>
                        <div className="text-right">{formatTimestampByLanguage(pickUpData?.appointmentTime, i18n.language)}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500 text-[#6366f1]">{t('pages.pickUp.finished.failureTime')}:</div>
                        <div className="text-right text-[#6366f1]">{formatTimestampByLanguage(pickUpData?.actualPickupTime, i18n.language)}</div>
                    </div>
                    <div className="flex justify-between items-start mb-4">
                        <div className="text-gray-500 text-[#6366f1]" >{t('pages.pickUp.finished.failureReason')}:</div>
                        <div className="text-right flex-1 ml-4 text-[#6366f1]">{getPickupFailureReasonText(pickUpData?.exceptionReasonCode, i18n.language)}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.pickUp.pickedUp.remark')}:</div>
                        <div className="text-right break-all">{pickUpData?.remark || '-'}</div>
                    </div>
                </div>
            );
        }


        return null;
    };

    const renderPhotoRequirements = (): PhotoRequirement => {
        if (endPoint) {
            return {
                description: t('pages.pickUp.finished.handoverPhotoDesc'),
                items: [t('pages.pickUp.finished.receiptDocument'), t('pages.pickUp.finished.packagePhoto')]
            };
        } else if (pickUpSuccess) {
            return {
                description: t('pages.pickUp.finished.successPhotoDesc'),
                items: [t('pages.pickUp.finished.waybill'), t('pages.pickUp.finished.packagePlacement'), t('pages.pickUp.finished.doorNumber')]
            };
        } else if (pickUpFailed) {
            return {
                description: t('pages.pickUp.finished.failedPhotoDesc'),
                items: [t('pages.pickUp.pickUpFailed.photoDescDetail')]
            };
        }
        return {description: '', items: []};
    };

    const photoReq = renderPhotoRequirements();

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(PickupFinishedPath.PICKUP_FINISHED, {
                    state: {
                        selectedDate: selectedDate
                    }
                })}>
                    {pickUpData?.channelName}
                </NavBar>
            </div>

            {startPoint ? (
                <div className="flex-1 bg-white">
                    <div className="p-4">
                        <div className="font-bold mb-3">{t('pages.pickUp.finished.siteInfo')}</div>
                        <div className="flex items-center mb-3">
                            <div className="w-[90px] text-gray-500">{t('pages.pickUp.finished.site')}:</div>
                            <div className="flex-1 text-right">
                                {pickUpData?.site}
                            </div>
                        </div>
                        <div className="flex items-center mb-3">
                            <div className="w-[90px] text-gray-500">{t('pages.pickUp.finished.siteCode')}:</div>
                            <div className="flex-1 text-right">{t('pages.pickUp.start')}</div>
                        </div>
                        <div className="flex items-center mb-3">
                            <div className="w-[90px] text-gray-500">{t('pages.pickUp.finished.phone')}:</div>
                            <div className="flex-1 text-right">{`${pickUpData?.contactPhone}${pickUpData?.ext ? `-${pickUpData?.ext}` : ''}`} </div>
                        </div>
                        <div className="flex items-start mb-3">
                            <div className="w-[90px] text-gray-500">{t('pages.pickUp.finished.address')}:</div>
                            <div className="flex-1 text-right break-all">
                                {buildAddressFromAppointment(pickUpData || {})}
                            </div>
                        </div>
                        <div className="flex items-center mb-3">
                            <div className="w-[90px] text-gray-500">{t('pages.pickUp.finished.scheduledArrivalTime')}:</div>
                            <div className="flex-1 text-right">{formatTimestampByLanguage(pickUpData?.appointmentTime, i18n.language)}</div>
                        </div>
                        <div className="flex items-center mb-3">
                            <div className="w-[90px] text-gray-500">{t('pages.pickUp.finished.actualArrivalTime')}:</div>
                            <div className="flex-1 text-right text-[#6366f1]">{formatTimestampByLanguage(pickUpData?.actualTime, i18n.language)}</div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto bg-gray-100">
                    {renderDetailInfo()}
                    <div className="p-4 bg-white">
                        <div className="text-lg font-bold mb-4">{t('pages.pickUp.finished.photos')}</div>
                        <div className="text-gray-500 mb-4">
                            {photoReq.description}
                            <span className="text-green-600 text-sm">
                                {photoReq.items.join(t('pages.pickUp.finished.photoSeparator') as string)}
                            </span>
                            {t('pages.pickUp.finished.photoEndMark')}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            {pickUpDetailPhotos.map((photo, index) => (
                                <div key={index} className="aspect-square overflow-hidden bg-white border border-gray-200 rounded-lg">
                                    <Image
                                        src={photo}
                                        alt={t('pages.pickUp.finished.photoAlt') as string}
                                        fit="cover"
                                        className="w-full h-full object-cover"
                                        onClick={() => handleViewPhoto(photo)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {pickUpFailed && (
                <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto')}>
                    <Button
                        color="primary"
                        block
                        loading={loading}
                        onClick={() => setSuccessActionSheetVisible(true)}
                        className="flex-1"
                    >
                        {t('pages.pickUp.finished.pickupSuccess')}
                    </Button>
                </div>
            )}

            <ActionSheet
                visible={successActionSheetVisible}
                extra={
                    <div className="text-gray-500">
                        <h3 className={`text-center`}>{t('pages.pickUp.detail.successSelectTitle')}</h3>
                        <span>{t('pages.pickUp.detail.successSelectDesc')}</span>
                    </div>
                }
                cancelText={t('pages.pickUp.detail.cancel')}
                onClose={() => setSuccessActionSheetVisible(false)}
                actions={successSelectOps.map(unit => ({
                    text: (
                        <div className="text-center py-2">
                            <div className="font-medium text-lg" style={{color: 'var(--adm-color-primary)'}}>
                                {getUnitLabel(unit.value, i18n.language)}
                            </div>
                        </div>
                    ),
                    key: unit.value,
                    onClick: () => handleUnitSelection(unit.value)
                }))}
            />

            {/* 查看大图 */}
            <PickUpPhotoViewer
                photoUrl={viewingPhoto}
                onClose={() => setViewingPhoto(null)}
            />
        </div>
    );
};

export default PickupFinishedDetailPage;
